import React, { useState } from "react";
import { Switch } from "@radix-ui/react-switch";
import { PhoneInput2 } from "@/components/ui/phone-input2";
import { DynamicVariables } from "@/components/ui/DynamicVariables";
import { useDynamicVariablesQuery } from "@/components/hooks/useDynamicVariables";


interface VoiceCallInterfaceProps {
  agentName: string;
  icon?: string;
  voiceConfig: {
    baseUrl: string;
    url: string;
    user_id: string;
    agent_id: string;
    overide_previous_context: boolean;
    tenant_id: string;
    workspace_id: string;
  };
}

export const OutboundCallInterface: React.FC<VoiceCallInterfaceProps> = ({
  agentName,
  icon,
  voiceConfig
}) => {
  const [overrideContext, setOverrideContext] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [responseMsg, setResponseMsg] = useState("");
  const [callSubmitted, setCallSubmitted] = useState(false);
  const [isDynamicVariableSelectionOpen, setIsDynamicVariableSelectionOpen] =
    useState(false);
  const [dynamicVariables, setDynamicVariables] = useState<Record<string, any>>(
    {}
  );
  const { data: dynamicVarsResponse, isLoading: dynamicVarsLoading } = useDynamicVariablesQuery(voiceConfig, isDynamicVariableSelectionOpen);
    

  type DynamicVarConfig = {
    [key: string]: {
      label: string;
      type: string;
      required: boolean;
    };
  };

  const dynamicVarsConfig: DynamicVarConfig = {
    name: { label: "Rohan", type: "text", required: true },
    date: { label: "18th July 2025", type: "text", required: false }
  };

  const handleStartClick = () => {
    setIsDynamicVariableSelectionOpen(true);
  };

  const handleDynamicVariablesSubmit = (variables: Record<string, any>) => {
    setDynamicVariables(variables);
    setIsDynamicVariableSelectionOpen(false);
    submitForm(variables);
  };

  const submitForm = async (variables = {}) => {
    setIsLoading(true);
    setResponseMsg("");
    try {
      const response = await fetch(voiceConfig.baseUrl + "/voice/outbound-call", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "tenant-id": voiceConfig.tenant_id,
          "workspace-id": voiceConfig.workspace_id
        },
        body: JSON.stringify({
          number: phoneNumber,
          agent_id: voiceConfig.agent_id,
          overide_previous_context: overrideContext,
          custom_dynamic_variables_config: variables
        })
      });
      const data = await response.json();
      if (response.ok) {
        setCallSubmitted(true);
        setResponseMsg("You'll receive a call soon");
      } else {
        setResponseMsg("Failed to submit call request.");
      }
    } catch (error) {
      setResponseMsg("Error submitting call request.");
      console.error("Failed to submit call request:", error);
    }
    setIsLoading(false);
  };

  return (
    <>
      <link
        href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Manrope:wght@300;400;500;600&display=swap"
        rel="stylesheet"
      />
      <div className="bg-blue-950/40 p-0 rounded-[30px] sm:rounded-[48px]">
        <div
          className="m-4 sm:m-4 sm:pt-6 sm:pb-8 w-[80vw] md:w-[350px] h-[50vh] sm:h-[80vh] min-h-[400px] text-white flex flex-col relative overflow-hidden rounded-[2rem] sm:rounded-[3rem] before:absolute before:inset-[-6px] sm:before:inset-[-9px] before:border-[6px] sm:before:border-[9px] before:border-[#FFFFFF0D] before:rounded-[2rem] sm:before:rounded-[3rem] before:pointer-events-none before:bg-transparent"
          style={{
            background:
              "linear-gradient(220deg,#0d1b3d 0%, #1a237e 25%, #3949ab 50%, #42a5f5 75%,rgb(138, 217, 254) 100%)",
            fontFamily: "Manrope, sans-serif"
          }}
        >
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-10 left-6 w-28 h-28 bg-blue-500 rounded-full blur-3xl"></div>
            <div className="absolute bottom-24 right-6 w-36 h-36 bg-purple-500 rounded-full blur-3xl"></div>
          </div>

          <div className="flex-shrink-0 pt-4 sm:pt-6 text-center z-10 mb-1">
            <h1
              className="text-lg sm:text-xl md:text-2xl font-extrabold text-white drop-shadow"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              {agentName}
            </h1>
          </div>

          <div className="flex-1 flex flex-col items-center justify-center relative z-10 min-h-0">
            <div className="relative flex items-center justify-center">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-44 h-44 rounded-full bg-gradient-to-tr from-blue-400 via-cyan-400 to-purple-500 blur-2xl opacity-80"></div>
              </div>
              <div className="p-3 rounded-full bg-gradient-to-tr from-blue-400 to-cyan-300 shadow-lg relative z-10">
                <img
                  src={icon || "/default-avatar.png"}
                  alt="Agent Icon"
                  className="w-36 h-36 md:w-48 md:h-48 rounded-full object-cover"
                />
              </div>
            </div>
          </div>

          <div className="flex-shrink-0 pb-4 sm:pb-6 md:pb-8 px-4 sm:px-8 z-10">
            <div className="sound-visualizer-wrapper mb-3 sm:mb-4 flex justify-center">
              <div className="w-full">
                <div className="flex flex-col gap-6 items-center justify-center w-full bg-indigo-900/40 rounded-2xl shadow-lg p-6 border border-indigo-400/30 backdrop-blur-md">
                  <div className="flex items-center justify-between w-full">
                    <label
                      className="pl-4 font-bold text-white text-base sm:text-lg"
                      htmlFor="clear-context-switch"
                    >
                      Clear previous call history
                    </label>
                    <Switch
                      id="clear-context-switch"
                      checked={overrideContext}
                      onCheckedChange={() =>
                        setOverrideContext((prev: boolean) => !prev)
                      }
                      className={`
                        relative inline-flex items-center w-12 h-6
                        rounded-full transition-colors
                        border border-white/30
                        ${
                          overrideContext
                            ? "bg-cyan-400 border-cyan-400"
                            : "bg-gray-400 border-gray-400"
                        }
                      `}
                      style={{ outline: "none" }}
                    >
                      <span
                        className={`
                          inline-block w-6 h-6 bg-white rounded-full shadow
                          transform transition-transform
                          ${overrideContext ? "translate-x-6" : "translate-x-0"}
                        `}
                      />
                    </Switch>
                  </div>

                  <div className="w-full">
                    <PhoneInput2
                      className="text-white bg-indigo-800/30 rounded-lg focus:ring-2 focus:ring-cyan-400"
                      placeholder="Enter phone number"
                      phoneNumber={phoneNumber}
                      setPhoneNumber={setPhoneNumber}
                    />
                  </div>

                  <div className="w-full flex flex-col items-center mt-4 gap-2">
                    {callSubmitted ? (
                      <div className="flex flex-row items-center justify-center gap-1">
                        <span className="text-white text-base font-extrabold">
                          You'll receive a call soon
                        </span>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="bg-blue-500 text-xl font-black hover:bg-blue-700 from-cyan-400 to-blue-500 hover:from-cyan-500 hover:to-blue-600 text-white py-3 px-16 rounded-lg shadow-md transition-all text-base disabled:from-gray-500 disabled:to-gray-500 disabled:cursor-not-allowed"
                        disabled={phoneNumber.length < 10 || isLoading}
                        onClick={handleStartClick}
                      >
                        {isLoading ? "Submitting..." : "Start"}
                      </button>
                    )}
                    {responseMsg && !callSubmitted && (
                      <span className="text-sm text-white/80 mt-2">
                        {responseMsg}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {isDynamicVariableSelectionOpen && !dynamicVarsLoading && (
        Object.keys(dynamicVarsResponse || {}).length > 0 ? (
          <DynamicVariables
            isOpen={true}
            response={dynamicVarsResponse}
            onClose={() => setIsDynamicVariableSelectionOpen(false)}
            onSubmit={handleDynamicVariablesSubmit}
          />
        ) : (
          (() => {
            setIsDynamicVariableSelectionOpen(false);
            handleDynamicVariablesSubmit({});
            return null;
          })()
        )
      )}
        {/* {isDynamicVariableSelectionOpen && (
          <DynamicVariables
            isOpen={true}
            response={Object.keys(dynamicVarsConfig).reduce((acc, key) => {
              acc[key] = dynamicVarsConfig[key].label;
              return acc;
            }, {} as { [key: string]: string })}
            onClose={() => setIsDynamicVariableSelectionOpen(false)}
            onSubmit={handleDynamicVariablesSubmit}
          />
        )} */}
      </div>
    </>
  );
};
